/**
 * Estate-wide engagement and funnel figures, from the dedicated metrics
 * endpoints (avrio-backend#86/#88) rather than a per-job fan-out. Two
 * requests total, independent of job count -- unlike useDashboardData, this
 * never caps or skips.
 *
 * Deliberately NOT surfacing `job_match_rate` or `days_to_fill`. Read
 * directly from avrio-backend origin/main MetricsService:
 *   - getEngagementMetrics() computes job match rate as
 *     `totalMatched / Math.max(totalMatched, 1)`, which is always ~100 --
 *     the method's own comment calls it a "placeholder".
 *   - getJobMetrics() sets `daysToFill = 0` unconditionally, with a TODO to
 *     read the job's posting date.
 * Both return 200 with plausible-looking numbers, so this is not a fetch
 * failure a caller would notice -- it has to be caught by reading the
 * service, which is why it is written down here rather than assumed fixed
 * later. Flagged upstream 2026-09-16; re-verify the backend source before
 * wiring either field up, do not take this comment's absence as done.
 */

import { useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

export interface EngagementMetrics {
  totalApplicants: number;
  totalMatched: number;
  /** Percentage, 0-100. */
  responseRate: number;
}

export interface EngagementState {
  engagement: EngagementMetrics | null;
  /** Stage -> count, keyed the same as PIPELINE_STAGES. Terminal stages are not included. */
  funnelStages: Record<string, number> | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: EngagementState = {
  engagement: null,
  funnelStages: null,
  loading: true,
  error: null,
};

export const useEngagementMetrics = (): EngagementState => {
  const [data, setData] = useState<EngagementState>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setData({ ...EMPTY, loading: true });
      try {
        const [engagementRes, funnelRes] = await Promise.all([
          axios.get('/api/metrics/engagement'),
          axios.get('/api/metrics/funnel'),
        ]);
        if (cancelled) return;
        setData({
          engagement: {
            totalApplicants: engagementRes.data?.total_applicants ?? 0,
            totalMatched: engagementRes.data?.total_matched ?? 0,
            responseRate: engagementRes.data?.response_rate ?? 0,
          },
          funnelStages: funnelRes.data?.stages ?? {},
          loading: false,
          error: null,
        });
      } catch (e) {
        if (!cancelled) {
          setData({
            engagement: null,
            funnelStages: null,
            loading: false,
            error: 'Could not load engagement metrics.',
          });
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
};
